import React, { useEffect, useState } from 'react';
import { paymentAPI } from '../../api';

const statusStyles = {
  SUBMITTED: 'bg-yellow-100 text-yellow-800',
  VERIFIED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
};

const AdminPaymentHistory = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null); // payment _id being acted on
  const [rejectNote, setRejectNote] = useState({}); // { [paymentId]: 'note' }
  const [showRejectBox, setShowRejectBox] = useState({}); // { [paymentId]: bool }
  const [toast, setToast] = useState('');

  useEffect(() => {
    fetchPaymentHistory();
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  const fetchPaymentHistory = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await paymentAPI.getAdminPaymentHistory();
      setPayments(response?.data?.data || []);
    } catch (fetchError) {
      console.error('Failed to fetch admin payment history:', fetchError);
      setError(fetchError?.response?.data?.error || 'Failed to load payment history');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (payment) => {
    if (!window.confirm(`Approve ₹${Number(payment.amount).toLocaleString()} for ${payment.userId?.name}?\n\nThis will immediately credit their deposit wallet.`)) return;
    try {
      setActionLoading(payment._id);
      await paymentAPI.approvePayment(payment._id, { adminNote: 'Approved and wallet credited' });
      showToast(`✅ ₹${Number(payment.amount).toLocaleString()} credited to ${payment.userId?.name}'s deposit wallet.`);
      await fetchPaymentHistory();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to approve payment');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (payment) => {
    if (!window.confirm(`Delete payment of ₹${Number(payment.amount).toLocaleString()} from ${payment.userId?.name}?\n\nThis will reverse the transaction and remove the payment record.`)) return;
    try {
      setActionLoading(payment._id);
      await paymentAPI.deletePayment(payment._id, { adminNote: 'Payment deleted and transaction reversed' });
      showToast(`🗑️ Payment deleted and transaction reversed.`);
      await fetchPaymentHistory();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to delete payment');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (payment) => {
    const note = rejectNote[payment._id]?.trim() || 'Rejected by admin';
    try {
      setActionLoading(payment._id);
      await paymentAPI.rejectPayment(payment._id, { adminNote: note });
      showToast(`❌ Payment from ${payment.userId?.name} rejected.`);
      setShowRejectBox((prev) => ({ ...prev, [payment._id]: false }));
      await fetchPaymentHistory();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to reject payment');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="bg-[#F8FAFC] p-6 rounded-2xl">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-[#0F2F3D] text-white px-5 py-3 rounded-xl shadow-xl text-sm animate-fade-in">
          {toast}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-3xl font-bold text-[#0F2F3D]">Payment History</h2>
          <p className="text-gray-500 mt-1">All user payment proof submissions — approve to auto-credit deposit wallet</p>
        </div>
        <button
          onClick={fetchPaymentHistory}
          className="px-4 py-2 bg-gradient-to-r from-[#E4572E] to-[#F47C4D] text-white rounded-lg shadow hover:opacity-90 transition-all duration-300"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#E4572E] border-t-transparent"></div>
          </div>
        ) : payments.length === 0 ? (
          <div className="text-center py-10 text-gray-500">No payment submissions found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0F2F3D] text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Sr. No</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">User</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">UPI ID</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Transaction ID</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Screenshot</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payments.map((payment, index) => (
                  <tr key={payment._id} className="hover:bg-gray-50 align-top">
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{index + 1}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {new Date(payment.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div className="font-medium text-[#0F2F3D]">{payment.userId?.name || '-'}</div>
                      <div className="text-xs text-gray-500">{payment.userId?.email || '-'}</div>
                      <div className="text-xs text-blue-600">{payment.userId?.mySponsorCode || '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-[#0F2F3D] whitespace-nowrap">
                      ₹{Number(payment.amount || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{payment.upiId}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{payment.transactionId || '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      <a
                        href={payment.screenshotUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        View
                      </a>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[payment.status] || statusStyles.SUBMITTED}`}>
                        {payment.status || 'SUBMITTED'}
                      </span>
                      {payment.adminNote && (
                        <div className="text-xs text-gray-400 mt-1 max-w-[120px]">{payment.adminNote}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex gap-1">
                        {payment.status === 'SUBMITTED' ? (
                          <div className="flex flex-col gap-1 min-w-[200px]">
                            <button
                              onClick={() => handleApprove(payment)}
                              disabled={actionLoading === payment._id}
                              className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                            >
                              {actionLoading === payment._id ? 'Processing...' : '✓ Approve & Credit'}
                            </button>

                            {showRejectBox[payment._id] ? (
                              <div className="flex flex-col gap-1 mt-1">
                                <input
                                  type="text"
                                  placeholder="Rejection reason (optional)"
                                  value={rejectNote[payment._id] || ''}
                                  onChange={(e) => setRejectNote((prev) => ({ ...prev, [payment._id]: e.target.value }))}
                                  className="px-2 py-1 border border-red-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-red-400"
                                />
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => handleReject(payment)}
                                    disabled={actionLoading === payment._id}
                                    className="flex-1 px-2 py-1 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                                  >
                                    Confirm Reject
                                  </button>
                                  <button
                                    onClick={() => setShowRejectBox((prev) => ({ ...prev, [payment._id]: false }))}
                                    className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => setShowRejectBox((prev) => ({ ...prev, [payment._id]: true }))}
                                disabled={actionLoading === payment._id}
                                className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 transition-colors disabled:opacity-50"
                              >
                                ✗ Reject
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={() => handleDelete(payment)}
                              disabled={actionLoading === payment._id}
                              className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                            >
                              {actionLoading === payment._id ? 'Processing...' : '🗑️ Delete & Reverse'}
                            </button>
                            <span className="text-xs text-gray-400 italic">
                              {payment.status === 'VERIFIED' ? 'Wallet credited' : 'Rejected'}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPaymentHistory;

